import type {
  AddProponentDataRequest,
  AddProponentDataResponse,
  CalculateProponentPremiumRequest,
  CalculateProponentPremiumResponse,
  GetProponentDataParams,
  GetProponentDataResponse,
  GetLayoutImportSegurVidaParams,
  GetLayoutImportSegurVidaResponse,
  GetProponentListParams,
  GetProponentListResponse,
  InactivateProponentDataParams,
  InactivateProponentDataResponse,
  ImportProponentBatchRequest,
  ImportProponentBatchResponse,
  UpdateProponentDataRequest,
  UpdateProponentDataResponse,
} from '../../types/vida/inclusionProponent/inclusionProponentType';
import { VIDA_PROPONENTE_MAINTENANCE_ROUTES } from '../requestsService';

export class MaintenanceProponentService {
  private fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>;

  constructor(fetch: <T>(endpoint: string, options?: RequestInit) => Promise<T>) {
    this.fetch = fetch;
  }

  /**
   * Includes proponent data.
   * Backend: /v1/bff/vida/proposta-adesao/incluir-dados-proponente
   */
  async addProponentData(
    request: AddProponentDataRequest,
  ): Promise<AddProponentDataResponse> {
    return this.fetch<AddProponentDataResponse>(
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_ADD_PROPONENTE_MAINTENANCE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Updates proponent data.
   * Backend: /v1/bff/vida/proposta-adesao/alterar-dados-proponente
   */
  async updateProponentData(
    request: UpdateProponentDataRequest,
  ): Promise<UpdateProponentDataResponse> {
    return this.fetch<UpdateProponentDataResponse>(
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_ALTER_PROPONENTE_MAINTENANCE,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Fetches proponent detail.
   * Backend: /v1/bff/vida/proposta-adesao/obter-dados-proponente
   */
  async getProponentData(
    params: GetProponentDataParams,
  ): Promise<GetProponentDataResponse> {
    const buildQueryString = (queryParams: Record<string, string | number | undefined>): string => {
      const search = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          search.append(key, String(value));
        }
      });
      const qs = search.toString();
      return qs ? `?${qs}` : '';
    };

    const url =
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_PROPONENTE_MAINTENANCE_DETAIL +
      buildQueryString({
        identSubestipulante: params.identSubestipulante,
        identProponente: params.identProponente,
        codCpf: params.codCpf,
        codCtrlCpf: params.codCtrlCpf,
      });

    return this.fetch<GetProponentDataResponse>(url, { method: 'GET' });
  }

  /**
   * Fetches paginated proponent list for a subestipulante.
   * Backend: /v1/bff/vida/proposta-adesao/obter-lista-proponente-subestipulante
   */
  async getProponentList(
    params: GetProponentListParams,
  ): Promise<GetProponentListResponse> {
    const buildQueryString = (queryParams: Record<string, string | number | undefined>): string => {
      const search = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          search.append(key, String(value));
        }
      });
      const qs = search.toString();
      return qs ? `?${qs}` : '';
    };

    const url =
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_LIST_PROPONENTE_MAINTENANCE +
      buildQueryString({
        identSubestipulante: params.identSubestipulante,
        NmProponente: params.nmProponente,
        CdCpf: params.cdCpf,
        CdCtrlCpf: params.cdCtrlCpf,
        CdMatricula: params.cdMatricula,
        page: params.page,
        size: params.size,
      });

    return this.fetch<GetProponentListResponse>(url, { method: 'GET' });
  }

  /**
   * Inactivates or deletes proponent data.
   * Backend: /v1/bff/vida/proposta-adesao/inativar-excluir-dados-proponente
   */
  async inactivateProponentData(
    params: InactivateProponentDataParams,
  ): Promise<InactivateProponentDataResponse> {
    const buildQueryString = (queryParams: Record<string, string | number | undefined>): string => {
      const search = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          search.append(key, String(value));
        }
      });
      const qs = search.toString();
      return qs ? `?${qs}` : '';
    };

    const url =
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_INATIVATE_PROPONENTE_MAINTENANCE +
      buildQueryString({
        identProponente: params.identProponente,
      });

    return this.fetch<InactivateProponentDataResponse>(url, { method: 'GET' });
  }

  /**
   * Fetches spreadsheet layout for vida insured import.
   * Backend: /v1/bff/vida/faturamento/obter-layout-import-segur-vida
   */
  async getLayoutImportSegurVida(
    params: GetLayoutImportSegurVidaParams,
  ): Promise<GetLayoutImportSegurVidaResponse> {
    const buildQueryString = (queryParams: Record<string, string | number | undefined>): string => {
      const search = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          search.append(key, String(value));
        }
      });
      const qs = search.toString();
      return qs ? `?${qs}` : '';
    };

    const url =
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.GET_LAYOUT_IMPORT_SEGUR_VIDA +
      buildQueryString({
        pcContrApolc: params.pcContrApolc,
        pcSubEstteApolc: params.pcSubEstteApolc,
        pcTpoLyoutImporSegurVida: params.pcTpoLyoutImporSegurVida,
      });

    return this.fetch<GetLayoutImportSegurVidaResponse>(url, { method: 'GET' });
  }

  /**
   * Imports proponent data in batch.
   * Backend: /v1/bff/vida/proposta-adesao/importar-dados-proponente-lote
   */
  async importProponentBatch(
    request: ImportProponentBatchRequest,
  ): Promise<ImportProponentBatchResponse> {
    return this.fetch<ImportProponentBatchResponse>(
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_IMPORT_PROPONENTE_MAINTENANCE_LOT,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
  }

  /**
   * Calculates proponent insurance premium value.
   * Backend: /v1/bff/vida/segurado/cotar-valor-premio-seguro
   */
  async calculateProponentPremium(
    request: CalculateProponentPremiumRequest,
  ): Promise<CalculateProponentPremiumResponse> {
    return this.fetch<CalculateProponentPremiumResponse>(
      VIDA_PROPONENTE_MAINTENANCE_ROUTES.POST_CALCULATE_PROPONENTE_PREMIUM,
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
    );
  }
}
